/**
 * Utility functions for converting between 0-indexed column numbers and Excel-style column letters.
 */

// Pre-generate column letters A-Z and AA-ZZ (702 columns total)
const COLUMN_LETTERS: string[] = (() => {
  const letters: string[] = [];
  // Generate A-Z (0-25)
  for (let i = 0; i < 26; i++) {
    letters.push(String.fromCharCode(65 + i));
  }
  // Generate AA-ZZ (26-701)
  for (let i = 0; i < 26; i++) {
    for (let j = 0; j < 26; j++) {
      letters.push(String.fromCharCode(65 + i) + String.fromCharCode(65 + j));
    }
  }
  return letters;
})();

/**
 * Convert a 0-indexed column number to an Excel-style column letter.
 * @param index - 0-indexed column number (0 = A, 1 = B, 25 = Z, 26 = AA, etc.)
 * @returns The column letter (e.g., "A", "B", "Z", "AA")
 * @throws Error if index is out of range
 */
export function columnIndexToLetter(index: number): string {
  if (index < 0 || index >= COLUMN_LETTERS.length) {
    throw new Error(
      `Column index ${index} is out of range (0-${COLUMN_LETTERS.length - 1})`,
    );
  }
  return COLUMN_LETTERS[index];
}

/**
 * Convert an Excel-style column letter to a 0-indexed column number.
 * @param letter - The column letter (e.g., "A", "B", "Z", "AA")
 * @returns 0-indexed column number (A = 0, B = 1, Z = 25, AA = 26, etc.)
 * @throws Error if letter is invalid
 */
export function columnLetterToIndex(letter: string): number {
  const upperLetter = letter.toUpperCase();
  const index = COLUMN_LETTERS.indexOf(upperLetter);
  if (index === -1) {
    throw new Error(`Invalid column letter: ${letter}`);
  }
  return index;
}

/**
 * Array of column indices 0-25 (for A-Z dropdown options)
 */
export const COLUMN_INDEX_OPTIONS: number[] = Array.from(
  { length: 26 },
  (_, i) => i,
);
